import './style.css';
import Input from '../../../Inputs/Input';
import Button from "../../../Buttons/Button";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { secondary } from "../../../../theme";
import { useApi } from "../../../../hooks/api";
import { WppSimulator } from '../WppSimulator';
import { Editor } from '@tinymce/tinymce-react';
import { useGlobal } from "../../../../hooks/global";
import { AppConstants } from '../../../../constants';
import { IOptionsProps } from "../../../../interfaces";
import { Column, Label, Line } from "../../../../style";
import { ModalLoading } from "../../../Loadings/ModalLoading";
import { ICONS_MAPPER } from '../../ModalMessageModel/form';
import { ContainerButtons, ContainerForm, Form, VariableButton } from "./style";
import { FixRequiredSelect } from "../../../Inputs/BaseSelects/FixRequiredSelect";
import { maskPhone } from '../../../Inputs/Input/mask';
import { VARIABLES } from '../../../../utils/variables';
import parsePhoneNumberFromString from 'libphonenumber-js';
import SelectCountry from '../../../Inputs/SelectCountry';
import { Country } from '../../../../utils/countries';

interface IFormSendWppMessageModalProps {
  onCancel: Function;
  onSave: Function;
  options: IOptionsProps[];
  defaultData?: {
    id: string;
    title: string;
    subject: string;
    type: string;
    text: string;
    name: string;
  } | null;
  phones: string[];
  variables: any;
}

function htmlToWhatsappText(html: any) {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  function decodeHtmlEntities(text: any) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  function traverseNodes(node: any) {
    let text = '';

    if (node.nodeType === Node.TEXT_NODE) {
      return decodeHtmlEntities(node.nodeValue);
    }

    for (let child of node.childNodes) {
      text += traverseNodes(child);
    }

    switch (node.nodeName) {
      case 'P':
        return text + '\n';
      case 'BR':
        return text + '\n';
      case 'STRONG':
      case 'B':
        return '*' + text + '*';
      case 'EM':
      case 'I':
        return '_' + text + '_';
      case 'U':
        return '~' + text + '~';
      case 'H1':
      case 'H2':
      case 'H3':
        return '\n*' + text + '*\n';
      case 'UL':
      case 'OL':
        return text.split('\n').map(item => '• ' + item).join('\n');
      case 'LI':
        return '\n• ' + text;
      default:
        return text;
    }
  }

  return traverseNodes(tempDiv).trim();
}

export const FormSendWppMessage: React.FC<IFormSendWppMessageModalProps> = (props) => {
  const { onSave, options, defaultData, variables } = props;

  const { theme, notify, notifyBottom } = useGlobal();
  const { api } = useApi();
  const [visualizationMode, setVisualizationMode] = useState('preview');
  const [message, setMessage] = useState(defaultData?.text ?? "");
  const [selectedMessageModel, setSelectedMessageModel] = useState<any>(options?.[0]?.value);

  const pn = parsePhoneNumberFromString(props?.phones?.[0] || '');
  const defaultDdi = `+${pn?.countryCallingCode}`;
  const defaultNational = pn?.formatNational();

  const [phone, setPhone] = useState(defaultNational);
  const [country, setCountry] = useState<Country>({
    ddi: defaultDdi,
    code: '',
    name: '',
    flag: '',
  });

  const [customVariables, setCustomVariables] = useState<any[]>(VARIABLES.map((v) => ({ ...v, text: variables[v.value] ?? '' })));
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [loadingCreate, setLoadingCreate] = useState(false);

  const onSubmit = useCallback(async () => {
    setLoadingCreate(true);

    const pn = parsePhoneNumberFromString(country?.ddi + phone);
    const ddi = `+${pn?.countryCallingCode}`;
    const national = pn?.formatNational();

    try {
      await api.post('/send-wpp-message', { number: `${ddi}${national}`, message: (htmlToWhatsappText(message)) });

      notify('Mensagem enviada com sucesso!', "success");

      onSave();
    } catch (err: any) {
      window.open(`https://api.whatsapp.com/send?phone=${ddi}${national}&text=${encodeURIComponent(htmlToWhatsappText(message))}`, '_blank');

      notify('Verifique sua conexão com o Whastapp!', "alert");
    }

    setLoadingCreate(false);
  }, [message, phone, country, api]);

  const handleStopPropagation = useCallback(
    (e: any) => {
      e?.preventDefault();
      e?.stopPropagation();
      const confirm = window.confirm("Deseja realmente enviar a mensagem?");
      if (confirm) onSubmit();
    },
    [onSubmit, api]
  );

  const copyText = (textToCopy: string) => {
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        notifyBottom("Variável copiada para área de tranferência!", "success");
      })
      .catch((err) => {
        notifyBottom("Erro ao copiar a variável para a área de tranferência!", "error");
      });
  };

  const handleEnableEdition = useCallback(() => {
    const result = window.confirm("Deseja realmente habilitar a edição do texto?");
    if (result) setVisualizationMode('text');
  }, [])

  useEffect(() => {
    const findedModel = options.find((item) => item.value === selectedMessageModel);
    if (findedModel) setMessage(findedModel?.this?.text)
  }, [selectedMessageModel])

  useEffect(() => {
    let newMessage = `${message}`;
    for (const variable of customVariables) {
      if (newMessage.includes(`{{${variable.value}}}`)) {
        newMessage = newMessage.replaceAll(`{{${variable.value}}}`, variable.text.replaceAll('\n', '<br/>'));
      }
    }
    if (newMessage !== message) setMessage(newMessage);
  }, [message])

  return (
    <>
      <Line style={{ flex: 1, overflow: "auto", flexDirection: "row", gap: 16, padding: 16, flexWrap: 'wrap' }}>
        <ContainerForm open style={{ flex: 4, paddingBottom: 0 }}>
          <Form onSubmit={handleStopPropagation}>
            <Column style={{ gap: 16, flex: 1 }}>
              <Column style={{ gap: 16 }}>
                <Line style={{ gap: 16, flexWrap: "wrap" }}>
                  <Column style={{ flex: 10 }}>
                    <Label>Modelo*</Label>
                    <FixRequiredSelect
                      placeholder=""
                      options={options}
                      value={options.find((item) => item.value === selectedMessageModel)}
                      onChange={(value: any) => setSelectedMessageModel(value?.value)}
                    />
                  </Column>
                  <Column style={{ flex: 1, minWidth: 85 }}>
                    <Label>DDI*</Label>
                    <SelectCountry
                      error={undefined}
                      theme={theme}
                      disabled={false}
                      className="form-control"
                      isAdm={true}
                      onChange={(country: any) => {
                        setCountry(
                          country,
                        );
                      }}
                      value={country}
                    />
                  </Column>
                  <Column style={{ flex: 10 }}>
                    <Label>Telefone*</Label>
                    <Input
                      placeholder=""
                      defaultValue={maskPhone(phone || '', country?.ddi || '')}
                      mask={country?.ddi === '+55' ? 'phone' : undefined}
                      onChange={(value: any) => setPhone(value)}
                      required
                    />
                  </Column>
                  {visualizationMode === 'preview' && (
                    <Column style={{ alignItems: "flex-end", justifyContent: "flex-end", flex: 1 }}>
                      <Button
                        onClick={handleEnableEdition}
                        background={secondary[theme]}
                        color={"#FFF"}
                        style={{ padding: 8, maxWidth: 150, fontSize: 14, whiteSpace: "nowrap" }}
                      >
                        Habilitar edição
                      </Button>
                    </Column>
                  )}
                </Line>
              </Column>
              <Line style={{ flexWrap: "wrap", gap: 16 }}>
                {visualizationMode === 'text' && (
                  <Column style={{ minWidth: 300, flex: 4, minHeight: 500 }}>
                    <Editor
                      apiKey={AppConstants.TINY_API_KEY}
                      value={message}
                      init={{
                        height: '100%',
                        width: '100%',
                        language: 'pt_BR',
                        menubar: false,
                        end_container_on_empty_block: true,
                        plugins: "fullscreen paste",
                        toolbar: 'bold italic strikethrough | fullscreen',
                        resize: false,
                      }}
                      onEditorChange={(value: string) => setMessage(value)}
                    />
                  </Column>
                )}
                <Column style={{ flex: 1, justifyContent: "center", alignItems: "center", width: "100%" }}>
                  <WppSimulator text={message} />
                </Column>
              </Line>
            </Column>
            <button
              className="display-none"
              type="submit"
              ref={submitButtonRef}
            />
          </Form>
        </ContainerForm>
        <div style={{ display: "flex", flex: 1, justifyContent: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 300, flex: 1 }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ fontWeight: "bold" }}>Variáveis</div>
              <div style={{ fontSize: 14 }}>
                Clique em uma variável para copiar a chave correspondente.
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 10, marginLeft: -10 }}>
              {customVariables.map((v) => (
                <VariableButton onClick={() => copyText(v.text)} key={v.value} theme={theme}>
                  {ICONS_MAPPER[v.type]} {v.label}
                </VariableButton>
              ))}
            </div>
          </div>
        </div>
      </Line>
      <ContainerButtons theme={theme}>
        <Button
          onClick={() => submitButtonRef.current?.click()}
          background={secondary[theme]}
          color={"#FFF"}
          style={{ padding: 8, maxWidth: 150, fontSize: 14 }}
          children={"Enviar mensagem"}
        />
      </ContainerButtons>

      <ModalLoading loading={loadingCreate} theme={theme} />
    </>
  );
};
